import { X } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

interface GrantTrialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (plan: string, trialEnd: string) => void;
  isPending?: boolean;
}

export default function GrantTrialModal({
  isOpen,
  onClose,
  onSubmit,
  isPending,
}: GrantTrialModalProps) {
  const [plan, setPlan] = useState('plus_monthly');
  const [trialEnd, setTrialEnd] = useState('');

  if (!isOpen) return null;

  const today = new Date().toISOString().split('T')[0];
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 1);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  const handleSubmit = () => {
    if (!trialEnd) return;
    if (trialEnd <= today) {
      alert('Trial end date must be in the future');
      return;
    }
    onSubmit(plan, trialEnd);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-3 sm:items-center"
      onClick={onClose}
    >
      <Card
        className="my-auto w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Grant Trial</CardTitle>
              <CardDescription>
                Provide a trial subscription to this user
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Plan Type</label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              {[
                { value: 'plus_monthly', label: 'Plus Monthly' },
                { value: 'plus_annual', label: 'Plus Annual' },
                { value: 'power_monthly', label: 'Power Monthly' },
                { value: 'power_annual', label: 'Power Annual' },
                { value: 'pro_monthly', label: 'Pro Monthly' },
                { value: 'pro2_monthly', label: 'Pro2 Monthly' },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setPlan(option.value)}
                  className={`px-4 py-2 rounded-md border transition-colors ${
                    plan === option.value
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-background hover:bg-muted'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">
              Trial End Date
            </label>
            <input
              type="date"
              min={today}
              max={maxDateStr}
              value={trialEnd}
              onChange={(e) => setTrialEnd(e.target.value)}
              className="w-full px-3 py-2 border rounded-md bg-background"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleSubmit}
              disabled={isPending || !trialEnd}
              className="flex-1"
            >
              {isPending ? 'Granting...' : 'Grant Trial'}
            </Button>
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
